import { useState, useCallback, useEffect, type ChangeEvent } from "react";
import {
  Drawer,
  TextInput,
  Button,
  Row,
  ResponsiveFlex,
  CollapsibleCard,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import {
  AccountNumberTextInput,
  ContentCard,
  StateSelector,
  ZipCodeTextInput,
  useNotificationContext,
} from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { useFetchRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients/requests";
import RoutingNumberInput from "../../RecipientDrawer/RoutingNumberInput";
import styles from "./WirePaymentsTableScreen.module.scss";

type WireUnsavedRecipientDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  recipient: API.Recipient | null;
  onUpdate?: (updatedRecipient: API.Recipient) => void;
};

type FormState = {
  name: string;
  accountNumber: string;
  routingNumber: string;
  streetAddress: string;
  city: string;
  regionCode: string;
  postalCode: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const validate = (values: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = "Name is required";
  }
  if (!values.accountNumber.trim()) {
    errors.accountNumber = "Account number is required";
  } else if (values.accountNumber.trim().length > 17) {
    errors.accountNumber = "Account number must be 17 characters or fewer";
  }
  if (!values.routingNumber.trim()) {
    errors.routingNumber = "Routing number is required";
  } else if (!/^\d{9}$/.test(values.routingNumber.trim())) {
    errors.routingNumber = "Routing number must be exactly 9 digits";
  }
  if (!values.streetAddress.trim()) {
    errors.streetAddress = "Street address is required";
  } else if (values.streetAddress.trim().length > 35) {
    errors.streetAddress = "Street address must be 35 characters or fewer";
  }
  if (!values.city.trim()) {
    errors.city = "City is required";
  }
  if (!values.regionCode) {
    errors.regionCode = "State is required";
  }
  if (!values.postalCode.trim()) {
    errors.postalCode = "Zip code is required";
  }
  return errors;
};

const WireUnsavedRecipientDrawer = ({
  isOpen,
  onClose,
  recipient,
  onUpdate,
}: WireUnsavedRecipientDrawerProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { send: refetchRecipients } = useFetchRecipients();

  const [form, setForm] = useState<FormState>({
    name: "",
    accountNumber: "",
    routingNumber: "",
    streetAddress: "",
    city: "",
    regionCode: "",
    postalCode: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Sync form state when recipient changes (opens) or clears (closes)
  useEffect(() => {
    if (recipient) {
      const dest = recipient.fedwire_destination;
      setForm({
        name: recipient.name || "",
        accountNumber: dest?.account_number || "",
        routingNumber: dest?.routing_number || "",
        streetAddress: dest?.address?.street_address || "",
        city: dest?.address?.city || "",
        regionCode: dest?.address?.region_code || "",
        postalCode: dest?.address?.postal_code || "",
      });
      setErrors({});
    }
  }, [recipient?.id]);

  const handleChange = useCallback(
    (field: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState) => (value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!recipient) return;

    setSubmitting(true);
    try {
      // 1. Update recipient name
      await ApiHttp.fetch(
        `recipients/${recipient.id}/`,
        { method: "PUT" },
        {
          name: form.name.trim(),
          type: recipient.type || "business",
          hidden: true,
        }
      );

      // 2. Update fedwire destination
      const fedwireId = recipient.fedwire_destination?.id;
      const wirePayload = {
        account_number: form.accountNumber.trim(),
        routing_number: form.routingNumber.trim(),
        recipient: recipient.id,
        address: {
          street_address: form.streetAddress.trim(),
          city: form.city.trim(),
          region_code: form.regionCode,
          postal_code: form.postalCode.trim(),
          country_code: "US",
        },
      };

      let wireResponse;
      if (fedwireId) {
        wireResponse = await ApiHttp.fetch(
          `fedwire_destinations/${fedwireId}/`,
          { method: "PUT" },
          wirePayload
        );
      } else {
        wireResponse = await ApiHttp.fetch(
          "fedwire_destinations/",
          { method: "POST" },
          wirePayload
        );
      }

      // Update the parent with the combined recipient data
      if (onUpdate) {
        const updatedRecipient: API.Recipient = {
          ...recipient,
          name: form.name.trim(),
          fedwire_destination:
            wireResponse as API.Recipient["fedwire_destination"],
        };
        onUpdate(updatedRecipient);
      }

      // Refresh recipients in Redux store
      refetchRecipients();

      sendNotification({
        type: "success",
        text: l10n.getString("wire-unsaved-recipient-drawer-success"),
      });
      onClose();
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("wire-unsaved-recipient-drawer-error"),
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    recipient,
    refetchRecipients,
    sendNotification,
    l10n,
    onClose,
    onUpdate,
  ]);

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={onClose}
      showControls={false}
      paddingSize="xl"
    >
      <div className="margin--bottom--l">
        <h2 className="fontSize--xl fontWeight--bold nds-sans">
          {l10n.getString("wire-unsaved-recipient-drawer-title")}
        </h2>
        <p className="fontSize--s fontColor--secondary margin--top--xs">
          {l10n.getString("wire-unsaved-recipient-drawer-subtitle")}
        </p>
      </div>

      <CollapsibleCard
        title={l10n.getString("wire-unsaved-recipient-drawer-section-wire")}
        isOpen
      >
        <ResponsiveFlex gapSize="xl">
          <ContentCard kind="plain" paddingSize="none">
            <p className={styles.sectionSubHeader}>
              {l10n.getString(
                "wire-unsaved-recipient-drawer-section-payment"
              )}
            </p>
            <ResponsiveFlex gapSize="s">
              <TextInput
                label={l10n.getString(
                  "wire-unsaved-recipient-drawer-label-name"
                )}
                value={form.name}
                onChange={handleChange("name")}
                error={errors.name}
              />
              <AccountNumberTextInput
                label={l10n.getString(
                  "wire-unsaved-recipient-drawer-label-account-number"
                )}
                value={form.accountNumber}
                onChange={handleFieldChange("accountNumber")}
                error={errors.accountNumber}
              />
              <RoutingNumberInput
                label={l10n.getString(
                  "wire-unsaved-recipient-drawer-label-routing-number"
                )}
                value={form.routingNumber}
                onChange={handleFieldChange("routingNumber")}
                routingType="fedwire"
                error={errors.routingNumber}
              />
            </ResponsiveFlex>
          </ContentCard>

          <ContentCard kind="plain" paddingSize="none">
            <p className={styles.sectionSubHeader}>
              {l10n.getString(
                "wire-unsaved-recipient-drawer-section-address"
              )}
            </p>
            <ResponsiveFlex gapSize="s">
              <TextInput
                label={l10n.getString(
                  "wire-unsaved-recipient-drawer-label-street-address"
                )}
                value={form.streetAddress}
                onChange={handleChange("streetAddress")}
                error={errors.streetAddress}
              />
              <TextInput
                label={l10n.getString(
                  "wire-unsaved-recipient-drawer-label-city"
                )}
                value={form.city}
                onChange={handleChange("city")}
                error={errors.city}
              />
              <Row gapSize="xs">
                <Row.Item className={styles.stateGrow}>
                  <StateSelector
                    label={l10n.getString(
                      "wire-unsaved-recipient-drawer-label-state"
                    )}
                    value={form.regionCode}
                    onChange={handleFieldChange("regionCode")}
                    error={errors.regionCode}
                  />
                </Row.Item>
                <Row.Item className={styles.zipShrink}>
                  <ZipCodeTextInput
                    label={l10n.getString(
                      "wire-unsaved-recipient-drawer-label-zip-code"
                    )}
                    value={form.postalCode}
                    onChange={handleFieldChange("postalCode")}
                    error={errors.postalCode}
                  />
                </Row.Item>
              </Row>
            </ResponsiveFlex>
          </ContentCard>

          <Row justifyContent="end">
            <Button onClick={handleSubmit} disabled={submitting}>
              {l10n.getString(
                "wire-unsaved-recipient-drawer-button-update"
              )}
            </Button>
          </Row>
        </ResponsiveFlex>
      </CollapsibleCard>
    </Drawer>
  );
};

export default WireUnsavedRecipientDrawer;
